import api from './api';

// 클라이언트 인터랙션 타입 정의 (CMS용 단순화)
export interface ClientInteractionItem {
  id: number;
  title: string;
  description: string;
  isActive: boolean;
  isStandalone: boolean;
}

export interface ClientInteractionStats {
  total: number;
  active: number;
  inactive: number;
  version: string;
  hasData: boolean;
}

export interface ClientInteractionListResponse {
  data: ClientInteractionItem[];
  stats: ClientInteractionStats;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface VersionInfo {
  version: string;
  totalCount: number;
  activeCount: number;
  inactiveCount: number;
  lastUpdated: string | null;
}

class ClientInteractionService {
  private baseURL = '/client-interactions';

  // CMS용 인터랙션 목록 조회 (읽기 전용)
  async getInteractionList(page: number = 1, limit: number = 20, search?: string): Promise<ClientInteractionListResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search) {
        params.append('search', search);
      }

      const response = await api.get(`${this.baseURL}?${params.toString()}`);
      return {
        data: response.data.data,
        stats: response.data.stats,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Failed to fetch client interaction list:', error);
      throw error;
    }
  }

  // 인터랙션 활성화 상태 토글 (CMS에서 유일한 수정 기능)
  async toggleInteractionStatus(id: number, isActive?: boolean): Promise<ClientInteractionItem> {
    try {
      const requestData = isActive !== undefined ? { isActive } : {};
      const response = await api.patch(`${this.baseURL}/${id}/toggle`, requestData);
      return response.data.data;
    } catch (error) {
      console.error(`Failed to toggle interaction ${id} status:`, error);
      throw error;
    }
  }

  // 버전 정보 조회 (목록 조회 응답에서 추출)
  async getVersionInfo(): Promise<VersionInfo | null> {
    try {
      const response = await this.getInteractionList(1, 1); // 최소 데이터로 stats만 가져오기
      const stats = response.stats;

      if (!stats.version) {
        return null;
      }

      return {
        version: stats.version,
        totalCount: stats.total,
        activeCount: stats.active,
        inactiveCount: stats.inactive,
        lastUpdated: null // 목록 API에서 제공하지 않음
      };
    } catch (error) {
      console.error('Failed to fetch version info:', error);
      throw error;
    }
  }

  // 샘플 데이터가 없는 경우를 위한 메시지 생성
  getEmptyStateMessage(): string {
    return '클라이언트에서 동기화된 인터랙션이 없습니다. 클라이언트 앱에서 인터랙션을 생성하고 동기화해주세요.';
  }

  // 버전 포맷 유틸리티
  formatVersion(version: string): string {
    if (!version) return '버전 정보 없음';

    // v.20250929.001 형태를 "2025년 9월 29일 v001"로 변환
    const match = version.match(/v\.(\d{4})(\d{2})(\d{2})\.(\d{3})/);
    if (match) {
      const [, year, month, day, versionNum] = match;
      return `${year}년 ${parseInt(month)}월 ${parseInt(day)}일 v${versionNum}`;
    }

    return version;
  }

  // 마지막 업데이트 시간 포맷 유틸리티
  formatLastUpdated(lastUpdated: string | null): string {
    if (!lastUpdated) return '업데이트 정보 없음';

    const date = new Date(lastUpdated);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return '방금 전';
    if (diffInMinutes < 60) return `${diffInMinutes}분 전`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}시간 전`;

    return date.toLocaleDateString('ko-KR');
  }
}

const clientInteractionService = new ClientInteractionService();
export default clientInteractionService;